<!--
  OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
  all the essential functionalities required for any enterprise.
  Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com

  OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
  the GNU General Public License as published by the Free Software Foundation, either
  version 3 of the License, or (at your option) any later version.

  OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU General Public License for more details.

  You should have received a copy of the GNU General Public License along with OrangeHRM.
  If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="orangehrm-background-container cal-page">
    <div class="cal-container">
      <!-- Header -->
      <CalendarHeader
        :year="currentYear"
        :month="currentMonth"
        :is-admin="hasAdminAccess"
        @prev="prevMonth"
        @next="nextMonth"
        @add-event="openCreateEvent"
        @add-holiday="openHolidayDialog"
        @import-holidays="openHolidayImport"
      />

      <div class="cal-main-area">
        <!-- Loading State -->
        <div v-if="isLoading" class="cal-state-overlay">
          <div class="cal-spinner" />
          <p class="cal-state-text">Loading calendar…</p>
        </div>

        <!-- Month Grid -->
        <MonthView
          v-else
          :days="calendarDays"
          :events-by-day="eventsByDay"
          :holidays-by-day="holidaysByDay"
          :workweek="workweek"
          :is-admin="hasAdminAccess"
          @day-click="onDayClick"
          @event-click="onEventClick"
          @holiday-click="onHolidayClick"
          @add-holiday-date="openHolidayDialogWithDate"
        />

        <!-- Event Side Sheet -->
        <EventSideSheet
          v-if="sideSheetOpen"
          :event="selectedEvent"
          :pre-date="preDate"
          :workweek="workweek"
          :holidays-by-day="holidaysByDay"
          :is-admin="hasAdminAccess"
          @close="closeSideSheet"
          @saved="onEventSaved"
          @deleted="onEventDeleted"
        />
      </div>

      <!-- Holiday Dialog (View/Edit, Add, Import) -->
      <HolidayDialog
        v-if="holidayDialogOpen"
        :mode="holidayDialogMode"
        :holiday="selectedHoliday"
        :all-holidays="selectedDayHolidays"
        :pre-date="holidayPreDate"
        :current-year="currentYear"
        :is-admin="hasAdminAccess"
        @close="holidayDialogOpen = false"
        @saved="onHolidaySaved"
        @deleted="onHolidayDeleted"
      />
    </div>
  </div>
</template>

<script>
import {ref, computed, onMounted, inject} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {
  getCalendarDays,
  groupEventsByDay,
  groupHolidaysByDay,
  formatDateKey,
  isNonWorkingDay,
} from '../../utils/calendarUtils';
import CalendarHeader from '../../components/CalendarHeader.vue';
import MonthView from '../../components/MonthView.vue';
import EventSideSheet from '../../components/EventSideSheet.vue';
import HolidayDialog from '../../components/HolidayDialog.vue';

export default {
  name: 'CalendarPage',

  components: {
    CalendarHeader,
    MonthView,
    EventSideSheet,
    HolidayDialog,
  },

  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
    empNumber: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const today = new Date();
    const currentYear = ref(today.getFullYear());
    const currentMonth = ref(today.getMonth() + 1);

    const isLoading = ref(true);
    const events = ref([]);
    const holidays = ref([]);
    const workweek = ref(null);

    const sideSheetOpen = ref(false);
    const selectedEvent = ref(null); // null = create mode
    const preDate = ref(null);

    // Permissions injected from Layout.vue & backend prop fallback
    const permissions = inject('permissions', {});
    const hasAdminAccess = computed(() => {
      if (props.isAdmin) return true;
      if (permissions?.['apiv2_calendar_events']?.canCreate) return true;
      try {
        return (window.appGlobal?.userRoles ?? []).includes('Admin');
      } catch {
        return false;
      }
    });

    const feedHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/calendar/feed',
    );

    const calendarDays = computed(() =>
      getCalendarDays(currentYear.value, currentMonth.value),
    );
    const eventsByDay = computed(() => groupEventsByDay(events.value));
    const holidaysByDay = computed(() => groupHolidaysByDay(holidays.value));

    // Request ID counter to avoid race conditions when clicking through months quickly
    let currentFeedRequestId = 0;

    async function loadFeed() {
      const requestId = ++currentFeedRequestId;
      isLoading.value = true;
      try {
        const res = await feedHttp.getAll({
          year: currentYear.value,
          month: currentMonth.value,
        });
        if (requestId !== currentFeedRequestId) return; // Stale request, ignore!
        const payload = res.data?.data?.[0] ?? {};
        events.value = payload.events ?? [];
        holidays.value = payload.holidays ?? [];
        workweek.value = payload.workweek ?? null;
      } catch {
        if (requestId === currentFeedRequestId) {
          events.value = [];
          holidays.value = [];
          workweek.value = null;
        }
      } finally {
        if (requestId === currentFeedRequestId) {
          isLoading.value = false;
        }
      }
    }

    function loadAll() {
      return loadFeed();
    }

    function prevMonth() {
      if (currentMonth.value === 1) {
        currentMonth.value = 12;
        currentYear.value -= 1;
      } else {
        currentMonth.value -= 1;
      }
      loadFeed();
    }

    function nextMonth() {
      if (currentMonth.value === 12) {
        currentMonth.value = 1;
        currentYear.value += 1;
      } else {
        currentMonth.value += 1;
      }
      loadFeed();
    }

    function openCreateEvent(date) {
      selectedEvent.value = null;
      preDate.value = date || null;
      sideSheetOpen.value = true;
    }

    function onDayClick(day) {
      if (!hasAdminAccess.value) return;
      const isHoli = (holidaysByDay.value.get(day.dateKey) || []).length > 0;
      const isNonWork = isNonWorkingDay(day.date, workweek.value);
      if (isHoli || isNonWork) return; // Block event creation on holidays & non-working days
      openCreateEvent(day.date);
    }

    function onEventClick(event) {
      selectedEvent.value = event;
      preDate.value = null;
      sideSheetOpen.value = true;
    }

    function closeSideSheet() {
      sideSheetOpen.value = false;
      selectedEvent.value = null;
      preDate.value = null;
    }

    function onEventSaved() {
      closeSideSheet();
      loadFeed();
    }

    function onEventDeleted() {
      closeSideSheet();
      loadFeed();
    }

    // Holiday dialog states
    const holidayDialogOpen = ref(false);
    const holidayDialogMode = ref('view'); // 'view' | 'add' | 'import'
    const holidayPreDate = ref(new Date());
    const selectedHoliday = ref(null);
    const selectedDayHolidays = ref([]);

    function openHolidayDialog(date) {
      holidayPreDate.value = date instanceof Date ? date : new Date();
      holidayDialogMode.value = 'add';
      selectedHoliday.value = null;
      selectedDayHolidays.value = [];
      holidayDialogOpen.value = true;
    }

    function openHolidayDialogWithDate(payload) {
      const targetDate = payload?.date || payload;
      const defaultName = payload?.name || '3rd Saturday Off';
      holidayPreDate.value =
        targetDate instanceof Date ? targetDate : new Date(targetDate);
      holidayDialogMode.value = 'add';
      selectedHoliday.value = {
        name: defaultName,
        date: formatDateKey(holidayPreDate.value),
        length: 0,
        recurring: false,
      };
      holidayDialogOpen.value = true;
    }

    function openHolidayImport() {
      holidayPreDate.value = new Date();
      holidayDialogMode.value = 'import';
      selectedHoliday.value = null;
      selectedDayHolidays.value = [];
      holidayDialogOpen.value = true;
    }

    function onHolidayClick(dayHolidays) {
      selectedHoliday.value = dayHolidays?.[0] || null;
      selectedDayHolidays.value = dayHolidays || [];
      holidayPreDate.value = dayHolidays?.[0]?.date
        ? new Date(dayHolidays[0].date)
        : new Date();
      holidayDialogMode.value = 'view';
      holidayDialogOpen.value = true;
    }

    function onHolidaySaved() {
      holidayDialogOpen.value = false;
      loadFeed();
    }

    function onHolidayDeleted() {
      holidayDialogOpen.value = false;
      loadFeed();
    }

    onMounted(() => {
      loadAll();
    });

    return {
      currentYear,
      currentMonth,
      isLoading,
      hasAdminAccess,
      calendarDays,
      eventsByDay,
      holidaysByDay,
      workweek,
      sideSheetOpen,
      selectedEvent,
      preDate,
      holidayDialogOpen,
      holidayDialogMode,
      holidayPreDate,
      selectedHoliday,
      selectedDayHolidays,
      prevMonth,
      nextMonth,
      openCreateEvent,
      onDayClick,
      onEventClick,
      closeSideSheet,
      onEventSaved,
      onEventDeleted,
      openHolidayDialog,
      openHolidayDialogWithDate,
      openHolidayImport,
      onHolidayClick,
      onHolidaySaved,
      onHolidayDeleted,
    };
  },
};
</script>

<style src="./calendar.scss" lang="scss"></style>
